import { useMemo } from "react";
import { animated, useSpring } from "@react-spring/web";

import styles from "./Answer.module.css";
import AnswerModeIcon from "./AnswerIcon";
import { AnswerMode } from "../../answer-modes/AnswerModeContainer/AnswerMode";
import MarkdownRenderer from "./AnswerMarkdownRenderer";
import { ProcessStep } from "../../../api";

interface Props {
  answerMode: AnswerMode;
  reasoningResponse?: string;
  showReasoningSummary: boolean;
  processSteps?: ProcessStep[];
}

const REASONING_STEP_TYPES = new Set(["tool_reasoning", "handoff_summary"]);
const isReasoningStep = (step: ProcessStep) =>
  REASONING_STEP_TYPES.has(step.type);

interface ReasoningGroup {
  reasoning: ProcessStep | null;
  tools: ProcessStep[];
}

function groupStepsByReasoning(steps: ProcessStep[]): ReasoningGroup[] {
  const groups: ReasoningGroup[] = [];
  let current: ReasoningGroup = { reasoning: null, tools: [] };

  for (const step of steps) {
    if (isReasoningStep(step)) {
      // Push previous group if it has anything
      if (current.reasoning || current.tools.length > 0) {
        groups.push(current);
      }
      current = { reasoning: step, tools: [] };
    } else {
      current.tools.push(step);
    }
  }

  // Push final group
  if (current.reasoning || current.tools.length > 0) {
    groups.push(current);
  }

  return groups;
}

export const AnswerLoading = ({
  answerMode,
  reasoningResponse,
  showReasoningSummary,
  processSteps,
}: Props) => {
  const animatedStyles = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
  });

  const groups = useMemo(
    () => groupStepsByReasoning(processSteps ?? []),
    [processSteps],
  );

  return (
    <animated.div style={{ ...animatedStyles }}>
      <div className={styles.loadingContainer}>
        <div className={styles.loadingHeader}>
          <AnswerModeIcon answerMode={answerMode} isLoading={true} />
        </div>

        {groups.length > 0 ? (
          <div className={styles.stepsContainer}>
            {groups.map((group, gi) => {
              const isLastGroup = gi === groups.length - 1;
              const latestTool =
                group.tools.length > 0
                  ? group.tools[group.tools.length - 1]
                  : null;
              const toolCount = group.tools.length;

              return (
                <div key={gi} className={styles.stepGroup}>
                  {/* Reasoning text */}
                  {group.reasoning && (
                    <div className={styles.reasoningLine}>
                      <MarkdownRenderer
                        decodedContent={group.reasoning.details}
                        isStreaming={false}
                        compact
                      />
                    </div>
                  )}

                  {/* Tool line underneath reasoning */}
                  {isLastGroup && latestTool ? (
                    <div className={styles.activeToolLine}>
                      <span className={styles.stepDotActive} />
                      {toolCount > 1 && (
                        <span className={styles.toolCounter}>
                          {toolCount} tool{toolCount !== 1 ? "s" : ""}
                        </span>
                      )}
                      <span className={styles.activeToolLabel}>
                        {latestTool.label}
                      </span>
                    </div>
                  ) : !isLastGroup && toolCount > 0 ? (
                    <div className={styles.completedToolLine}>
                      <span className={styles.stepDot} />
                      <span className={styles.completedToolLabel}>
                        {toolCount} tool{toolCount !== 1 ? "s" : ""} used
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          /* Fallback when nothing yet */
          <div className={styles.stepsContainer}>
            <div className={styles.placeholderLine}>
              <span className={styles.stepDotActive} />
              <span className={styles.stepSummary}>Thinking…</span>
            </div>
          </div>
        )}

        {/* Reasoning summary block */}
        {!!reasoningResponse && showReasoningSummary && (
          <div className={styles.reasoningBlock}>
            <MarkdownRenderer
              decodedContent={reasoningResponse}
              isStreaming={false}
              compact
            />
          </div>
        )}
      </div>
    </animated.div>
  );
};
